/**
 * Minimal device cache of the server conversation LIST only (audit P-12).
 *
 * Shared station PCs must never show one login's history to the next. We keep at
 * most the conversation ids + titles for the signed-in user (to paint the
 * sidebar instantly before the network answers); message bodies are never
 * stored on disk. The cache is scoped per Guardian userId and wiped on sign-out.
 */
import { safeStorage } from './safeStorage';

const LIST_KEY_PREFIX = 'guardianHistoryList';
const MAX_CACHED = 50;

function listKey(userId) {
  return `${LIST_KEY_PREFIX}_${userId}`;
}

/** @returns {Array<{conversationId:string,title:string}>} */
export function loadCachedList(userId) {
  if (!userId) return [];
  const raw = safeStorage.get(listKey(userId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((e) => e && typeof e.conversationId === 'string')
      .slice(0, MAX_CACHED)
      .map((e) => ({
        conversationId: e.conversationId,
        title: typeof e.title === 'string' ? e.title : '',
      }));
  } catch {
    return [];
  }
}

/** Stores ids + titles only (never message bodies). */
export function saveCachedList(userId, items) {
  if (!userId || !Array.isArray(items)) return;
  const minimal = items
    .filter((e) => e && typeof e.conversationId === 'string')
    .slice(0, MAX_CACHED)
    .map((e) => ({ conversationId: e.conversationId, title: typeof e.title === 'string' ? e.title : '' }));
  safeStorage.set(listKey(userId), JSON.stringify(minimal));
}

/** Wipes this user's cached conversation list from the device (sign-out, P-12). */
export function clearGuardianHistoryCache(userId) {
  if (!userId) return;
  safeStorage.remove(listKey(userId));
}
